import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Rating,
  TextField,
  Typography,
  Box,
  Alert,
  IconButton,
  Divider,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { XIcon, ThumbsUpIcon, ThumbsDownIcon, BugIcon, LightbulbIcon, DotsThreeIcon } from '@phosphor-icons/react';
import { CheckCircleOutline } from '@mui/icons-material';
import CustomButton from '../widgets/CustomButton';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';
import { importAnalyticsService, FeedbackSubmission } from '../../services/importAnalyticsService';
import { useAuth } from '../../contexts/AuthContext';
import { APP_VERSION_LABEL } from '../../constants/appRelease';
import { font } from '../../utils/fontHelper';
import { glassStyles } from '../../tokens/designTokens';

interface ImportFeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  sessionId?: string;
  importSource: 'screenshot' | 'text' | 'link' | 'recommendation';
  importResults?: {
    moviesDetected: number;
    moviesSuccessful: number;
    processingTime: number;
  };
  variant?: 'import' | 'recommendation';
}

type Category = FeedbackSubmission['category'] | 'did_not_work';

const IMPORT_CATEGORIES: Array<{ key: Category; label: string; icon: React.ReactElement }> = [
  { key: 'praise', label: 'Worked Great!', icon: <ThumbsUpIcon size={15} weight="bold" /> },
  { key: 'did_not_work', label: 'Did Not Work', icon: <ThumbsDownIcon size={15} weight="bold" /> },
  { key: 'suggestion', label: 'Suggestion', icon: <LightbulbIcon size={15} weight="bold" /> },
  { key: 'bug', label: 'Bug Report', icon: <BugIcon size={15} weight="bold" /> },
  { key: 'other', label: 'Other', icon: <DotsThreeIcon size={15} weight="bold" /> },
];

const RECOMMENDATION_CATEGORIES: Array<{ key: Category; label: string; icon: React.ReactElement }> = [
  { key: 'praise', label: 'Great picks!', icon: <ThumbsUpIcon size={15} weight="bold" /> },
  { key: 'did_not_work', label: 'Off the mark', icon: <ThumbsDownIcon size={15} weight="bold" /> },
  { key: 'suggestion', label: 'Suggestion', icon: <LightbulbIcon size={15} weight="bold" /> },
  { key: 'bug', label: 'Something broke', icon: <BugIcon size={15} weight="bold" /> },
  { key: 'other', label: 'Other', icon: <DotsThreeIcon size={15} weight="bold" /> },
];

const RATING_LABELS: Record<number, string> = {
  1: 'Very Poor',
  2: 'Poor',
  3: 'Average',
  4: 'Good',
  5: 'Excellent',
};

const SOURCE_LABELS: Record<string, string> = {
  screenshot: 'Screenshot Import',
  text: 'Text Import',
  link: 'Link Import',
};

const ImportFeedbackDialog: React.FC<ImportFeedbackDialogProps> = ({
  open,
  onClose,
  sessionId,
  importSource,
  importResults,
  variant = 'import',
}) => {
  const isRecommendation = variant === 'recommendation';
  const FEEDBACK_CATEGORIES = isRecommendation ? RECOMMENDATION_CATEGORIES : IMPORT_CATEGORIES;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const [rating, setRating] = useState<number>(5);
  const [feedback, setFeedback] = useState('');
  const [category, setCategory] = useState<Category>('praise');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const needsComment = rating < 4 || category === 'did_not_work' || category === 'bug';
  const canSubmit = !isSubmitting && (!needsComment || feedback.trim().length >= 5);

  const handleSubmit = useCallback(async () => {
    if (!user?.uid) {
      setSubmitError('Missing session information');
      return;
    }
    // For recommendation variant, start a fresh session on submit
    const effectiveSessionId = sessionId || importAnalyticsService.startImportSession(user.uid, importSource);
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const resolvedCategory: FeedbackSubmission['category'] =
        category === 'did_not_work' ? 'bug' : category;
      await importAnalyticsService.submitFeedback(
        effectiveSessionId,
        user.uid,
        rating,
        category === 'did_not_work' ? `[Did Not Work] ${feedback}` : feedback,
        resolvedCategory,
        importSource,
        {
          userName: user.displayName ?? undefined,
          userEmail: user.email ?? undefined,
          appVersion: APP_VERSION_LABEL,
        }
      );
      setSubmitSuccess(true);
      setTimeout(onClose, 1800);
    } catch {
      setSubmitError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [sessionId, user?.uid, rating, feedback, category, importSource, onClose, isRecommendation]);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    setRating(5);
    setFeedback('');
    setCategory('praise');
    setSubmitError(null);
    setSubmitSuccess(false);
    onClose();
  }, [isSubmitting, onClose]);

  const getCategoryStyle = (key: Category) => {
    const selected = category === key;
    if (!selected) {
      return {
        border: `1.5px solid ${theme.palette.divider}`,
        color: theme.palette.text.secondary,
        bgcolor: 'transparent',
      };
    }
    const colorMap: Record<Category, string> = {
      praise: theme.palette.success.main,
      did_not_work: theme.palette.error.main,
      suggestion: theme.palette.info.main,
      bug: theme.palette.warning.main,
      other: theme.palette.primary.main,
    };
    return {
      border: `1.5px solid ${colorMap[key]}`,
      color: colorMap[key],
      bgcolor: `${colorMap[key]}18`,
    };
  };

  const dialogContent = (
    <>
      {/* Header — matches CreateListDialog pattern */}
      <BottomSheetDraggableHeader sx={{
        pt: 2.5,
        pb: isMobile ? 1 : 2,
        px: 3,
        fontFamily: font(),
        fontWeight: 600,
        fontSize: 18,
      }}>
        {isRecommendation ? 'How were your recommendations?' : 'How was your import?'}
        <Typography variant="body2" sx={{ fontFamily: font(), fontWeight: 400, color: theme.palette.text.secondary, mt: 0.25 }}>
          {isRecommendation
            ? 'Your feedback helps us improve recommendations'
            : [SOURCE_LABELS[importSource], importResults && `${importResults.moviesSuccessful} of ${importResults.moviesDetected} movies added`].filter(Boolean).join(' · ')
          }
        </Typography>
      </BottomSheetDraggableHeader>

      {/* Close button — absolute positioned, matches CreateListDialog */}
      <IconButton
        onClick={handleClose}
        disabled={isSubmitting}
        size="small"
        sx={{
          position: 'absolute',
          top: 18,
          right: 16,
          color: theme.palette.text.primary,
          zIndex: 1,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
          '&:hover': {
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          },
        }}
      >
        <XIcon size={20} />
      </IconButton>

      <Divider />

      <DialogContent sx={{ px: 3, py: 0 }}>
        {submitSuccess ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} py={4}>
            <CheckCircleOutline sx={{ fontSize: 52, color: theme.palette.success.main }} />
            <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 16, color: theme.palette.text.primary }}>
              Thanks for the feedback!
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontFamily: font(), textAlign: 'center' }}>
              This helps us improve the import experience.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2.5 }}>
            {/* Star Rating */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontFamily: font(), fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
                Overall Experience
              </Typography>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Rating
                  value={rating}
                  onChange={(_, v) => setRating(v || 1)}
                  size="large"
                  sx={{
                    '& .MuiRating-iconFilled': {
                      color: rating >= 4
                        ? theme.palette.success.main
                        : rating >= 3
                        ? theme.palette.warning.main
                        : theme.palette.error.main,
                    },
                  }}
                />
                <Typography variant="body2" sx={{ fontFamily: font(), fontWeight: 500, color: theme.palette.text.secondary }}>
                  {RATING_LABELS[rating]}
                </Typography>
              </Box>
            </Box>

            {/* Category Pills */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontFamily: font(), fontWeight: 600, mb: 1.5, color: theme.palette.text.primary }}>
                What best describes your experience?
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {FEEDBACK_CATEGORIES.map((cat) => (
                  <Box
                    key={cat.key}
                    onClick={() => setCategory(cat.key)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 10,
                      cursor: 'pointer',
                      fontFamily: font(),
                      fontSize: 13,
                      fontWeight: 500,
                      transition: 'all 0.15s ease',
                      userSelect: 'none',
                      ...getCategoryStyle(cat.key),
                    }}
                  >
                    {cat.icon}
                    {cat.label}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Comment Field */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontFamily: font(), fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
                Comments{' '}
                {needsComment
                  ? <span style={{ color: theme.palette.error.main }}>*</span>
                  : <span style={{ color: theme.palette.text.disabled, fontWeight: 400, fontSize: 12 }}>(optional)</span>
                }
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder={
                  category === 'did_not_work' ? "What went wrong? Which movies weren't found?"
                  : category === 'bug' ? 'Describe what happened...'
                  : category === 'suggestion' ? 'What would you like to see improved?'
                  : 'Any thoughts to share...'
                }
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: font(),
                    fontSize: 14,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    borderRadius: 2,
                    '& fieldset': { borderColor: theme.palette.divider },
                    '&:hover fieldset': { borderColor: theme.palette.text.secondary },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: '2px' },
                  },
                  '& .MuiInputBase-input::placeholder': { fontFamily: font() },
                }}
              />
            </Box>

            {submitError && <Alert severity="error">{submitError}</Alert>}
          </Box>
        )}
      </DialogContent>

      {!submitSuccess && (
        <DialogActions sx={{
          p: 3,
          pt: 1,
          pb: isMobile ? 'calc(env(safe-area-inset-bottom, 24px) + 16px)' : 3,
          display: 'flex',
          gap: 1.5,
        }}>
          <CustomButton
            onClick={handleClose}
            disabled={isSubmitting}
            variant="contained"
            semantic="tertiary"
            label="Skip"
            sx={{ flex: 1, py: 1.5, fontSize: 15, fontWeight: 600 }}
          />
          <CustomButton
            onClick={handleSubmit}
            variant="contained"
            semantic="brand"
            disabled={!canSubmit}
            label={isSubmitting ? '' : 'Submit Feedback'}
            icon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{
              flex: 1,
              py: 1.5,
              fontSize: 15,
              fontWeight: 600,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(0,0,0,0.4)'
                : '0 8px 32px rgba(125,122,255,0.2)',
            }}
          />
        </DialogActions>
      )}
    </>
  );

  return isMobile ? (
    <BottomSheet open={open} onClose={handleClose} maxHeight="90dvh">
      {dialogContent}
    </BottomSheet>
  ) : (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          ...glassStyles.popup(theme.palette.mode === 'dark'),
        },
      }}
    >
      {dialogContent}
    </Dialog>
  );
};

export default ImportFeedbackDialog;
